import { useState, useEffect } from 'react';
import { DateTimePicker } from '@mui/x-date-pickers';
import PropTypes from 'prop-types';
import { Grid, Button, Dialog, DialogTitle, DialogContent, IconButton, DialogActions, Typography, TextField, CircularProgress, Stack, Box } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Iconify from '../../../../reusable-components/iconify';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import CloseIconButton from '../../../../reusable-components/basic-buttons/CloseIconButton';
import axiosInstance from '../../../../utils/axios';
import { objectToFormData } from '../../../../api/utils';

SystemNoticeForm.propTypes = {
  viewMain: PropTypes.bool,
  editNotice: PropTypes.object,
  setSystemNotices: PropTypes.func,
  close: PropTypes.func,
  viewOnly: PropTypes.bool
};

export default function SystemNoticeForm({ close, setSystemNotices, viewMain, editNotice, viewOnly }) {
  const { enqueueSnackbar } = useSnackbar();

  const [messages, setMessages] = useState([{ id: -1, message: '' }]);
  const [title, setTitle] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setTitle(editNotice?.title || "");
    setMessages([{ id: -1, message: '' }]);
    setStartDate(editNotice?.startDate ? new Date(editNotice?.startDate) : null);
    setEndDate(editNotice?.endDate ? new Date(editNotice?.endDate) : null);
    if (editNotice) {
      setLoading(true);
      axiosInstance.get(`SystemNotice/SystemMessages?id=${editNotice.id}`)
        .then((res) => setMessages(res.data?.length > 0 ? res.data : [{ id: -1, message: '' }]))
        .catch(() => enqueueSnackbar("Failed to load data", { variant: "error" }))
        .finally(() => setLoading(false));
    }
  }, [editNotice]);

  const localToUTC = dateTime => {
    if (!dateTime) return null;
    dateTime = new Date(dateTime);
    const utcFromLocal = new Date(dateTime.getTime() - dateTime.getTimezoneOffset() * 60000);
    return utcFromLocal.toUTCString();
  };

  const UTCToLocal = dateTime => {
    if (!dateTime) return null;
    dateTime = new Date(dateTime);
    return new Date(dateTime.getTime() + dateTime.getTimezoneOffset() * 60000);
  };

  const handleSave = () => {
    setSaving(true);
    axiosInstance.post('SystemNotice/SystemNoticeForm', objectToFormData({
      id: editNotice?.id,
      title,
      complete: editNotice?.complete,
      startDate: localToUTC(startDate),
      endDate: localToUTC(endDate),
      viewMain,
      messages: messages?.filter(x => x.message).map((m) => ({ ...m, id: m.id > 0 ? m.id : 0 }))
    })).then(res => {
      res.data.startDate = UTCToLocal(res.data.startDate);
      res.data.endDate = UTCToLocal(res.data.endDate);
      setSystemNotices(notices => editNotice ? notices.map(notice => notice.id === res.data.id ? res.data : notice) : [res.data, ...notices]);
      enqueueSnackbar(`Notice ${(editNotice ? "updated" : "added")} successfully`, { variant: 'success' });
      close();
    }).catch(() => enqueueSnackbar(`Failed to ${(editNotice ? "update" : "add")} notice`, { variant: "error" }))
      .finally(() => setSaving(false));
  };

  return <Dialog open maxWidth="sm" fullWidth onClose={(event, reason) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
    close();
  }}>
    <DialogTitle sx={{ textAlign: 'center', mt: 2 }}>
      {editNotice ? viewOnly ? "View" : "Update" : "Add"} Notice <CloseIconButton onClick={close} />
    </DialogTitle>
    <DialogContent sx={{ height: '100%', width: '100%' }}>
      {!loading ? <Grid container direction="row" justifyContent="center" spacing={2}>
        <Grid item xs={12}>
          <Typography variant="subtitle1">
            Title*
          </Typography>
          <TextField value={title} onChange={(e) => setTitle(e.target.value)} multiline size="small" fullWidth disabled={viewOnly} autoComplete="off"/>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle1">
            Messages
            {!viewOnly && <IconButton color="primary" onClick={() => setMessages([...messages, { id: messages.length * -1 - 1, message: '' }])}>
              <Iconify icon="eva:plus-outline" />
            </IconButton>}
          </Typography>
          <Grid container direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            {messages?.map((message) => <Grid item xs={12} key={message.id}>
              <TextField
                value={message.message}
                onChange={(e) => setMessages(messages.map((m) => (m.id === message.id ? { ...m, message: e.target.value } : m)))}
                multiline
                size="small"
                fullWidth
                disabled={viewOnly}
                autoComplete="off"
              />
            </Grid>)}
          </Grid>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="subtitle1" sx={{ textAlign: 'left' }}>
            Start Date
          </Typography>
          <DateTimePicker
            slotProps={{
              textField: { size: 'small', sx: { width: '100%' } },
              actionBar: { actions: ['clear', 'cancel', 'accept'] },
            }}
            value={startDate || null}
            onChange={(dateValue) => setStartDate(dateValue)}
            disabled={viewOnly}
          />
        </Grid>
        <Grid item xs={6}>
          <Typography variant="subtitle1" sx={{ textAlign: 'left' }}>
            End Date
          </Typography>
          <DateTimePicker
            slotProps={{
              textField: { size: 'small', sx: { width: '100%' } },
              actionBar: { actions: ['clear', 'cancel', 'accept'] },
            }}
            value={endDate || null}
            onChange={(dateValue) => setEndDate(dateValue)}
            disabled={viewOnly}
          />
        </Grid>
      </Grid> : <Box textAlign="center"><CircularProgress sx={{ margin: '50px' }} /></Box>}
    </DialogContent>
    {!viewOnly && <DialogActions sx={{ justifyContent: 'center', mb: 2 }}>
      <Stack direction="row" spacing={2}>
        <Button
          variant="outlined"
          color="error"
          onClick={close}
          sx={{ width: '200px', boxShadow: 3, fontWeight: 'bold' }}
        >
          Cancel
        </Button>
        <LoadingButton
          loading={saving}
          variant="contained"
          color="primary"
          disabled={!title}
          onClick={handleSave}
          sx={{ width: '200px', boxShadow: 3, fontWeight: 'bold' }}
        >
          Save
        </LoadingButton>
      </Stack>
    </DialogActions>}
  </Dialog>;
}